import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider } from './hooks/useTheme';

import { CartProvider } from './context/CartContext';
import { UserProvider } from './context/UserContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { trackPageView, initFacebookPixel } from './utils/fbPixel';

import ShopShell from './components/ShopShell';
import ProductList from './components/ProductList';
import ProductDetails from './components/ProductDetails';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import NotFound from './components/NotFound';
import OrdersHistory from './components/OrdersHistory';
import AboutUs from './components/AboutUs';
import ProtectedRoute from './components/ProtectedRoute';
import PhoneVerification from './components/PhoneVerification';

// Component to fetch sale point config & initialize Facebook Pixel
const PixelInitializer = () => {
  const { businessName, salePointName } = useParams();
  const [pixelInitialized, setPixelInitialized] = useState(false);

  useEffect(() => {
    if (!businessName || !salePointName) return;

    fetch(`/api/salePointConfig/${businessName}/${salePointName}`)
        .then(res => res.json())
        .then(data => {
          if (data?.salePointPixelId && !window._fbPixelInitialized) {
            console.log('Initializing Facebook Pixel with ID:', data.salePointPixelId);
            initFacebookPixel(data.salePointPixelId);
            window._fbPixelInitialized = true;
            setPixelInitialized(true);
          } else {
            console.warn('No Pixel ID found for this sale point.');
          }
        })
        .catch(err => {
          console.error('Failed to fetch sale point config:', err);
        });
  }, [businessName, salePointName]);

  return null;
};

// PageViewTracker component to track page views
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    let timeoutId = null;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      console.log('Tracking page view for:', location.pathname);
      trackPageView();
    }, 300); // 300ms debounce

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location]);

  return null;
};

// AppRoutes component that includes PixelInitializer, PageViewTracker, and Routes
const AppRoutes = () => {
  return (
      <>
        <PixelInitializer />
        <PageViewTracker />
        <Routes>
          <Route path=":businessName/:salePointName/*" element={<ShopShell />}>
            <Route index element={<ProductList />} />
            <Route path="cart" element={<Cart />} />
            <Route path="product/:productId" element={<ProductDetails />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="orders-history" element={<ProtectedRoute><OrdersHistory /></ProtectedRoute>} />
            <Route path="about" element={<AboutUs />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/otp-verification" element={<PhoneVerification />} />
          <Route path="/not-found" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <UserProvider>
          <CartProvider>
            <AppRoutes />
            <ToastContainer
                position="top-right"
                autoClose={6000}
                hideProgressBar={false}
                newestOnTop={true}
                autoClose={6000}
                newestOnTop={true}
                closeOnClick={true}
                rtl={false}
                pauseOnFocusLoss={true}
                draggable={true}
                pauseOnHover={true}
                theme="colored"
            />
          </CartProvider>
        </UserProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
